import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYT3dSpline, FikoYT3dSplineHandles, ISplineAyarlari, SplineStili, SplineCizgiTipi, SplineTipi, CatmullRomTensionTipi } from './fikoyt-3d-spline';
import { FikoSahne } from '../../FikoSahne';
import { FikoYTService } from '@/src/service/fikoyt.service';

/**
 * Hazır şekil tipi
 */
export type HazirSekilTipi = 'serbest' | 'sinus' | 'spiral' | 'elips' | 'kalp' | 'sonsuzluk';

/**
 * FikoYT 3D Spline Dialog Component
 *
 * Spline (eğri) oluşturma ve düzenleme dialog'u.
 * Kontrol noktaları eklenebilir, silinebilir ve sürüklenerek düzenlenebilir.
 */
@Component({
    selector: 'fikoyt-3d-spline-dialog',
    templateUrl: './fikoyt-3d-spline-dialog.component.html',
    styleUrls: ['./fikoyt-3d-spline-dialog.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class FikoYT3dSplineDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    // ========================================================================
    // INPUT/OUTPUT
    // ========================================================================

    /** FikoDialogService pencere referansı */
    private _pencere?: any;
    get pencere(): any { return this._pencere; }
    @Input()
    set pencere(v: any) {
        const yeniPencere = v?.instance || v;
        const eskiPencere = this._pencere;
        this._pencere = yeniPencere;

        if (yeniPencere && eskiPencere) {
            if (this._duzenleModu && this._mevcutSpline) {
                this.dialogYenidenAcildi();
            } else if (!this._duzenleModu) {
                this.yeniSplineModuBaslat();
            }
        }
    }

    /** Ana gösterim referansı */
    @Input() anaGosterim?: any;

    /** Sahne referansı */
    @Input() sahne?: FikoSahne;

    /** Düzenleme modunda mı */
    private _duzenleModu: boolean = false;
    @Input()
    get duzenleModu(): boolean { return this._duzenleModu; }
    set duzenleModu(v: boolean) {
        const eskiDeger = this._duzenleModu;
        this._duzenleModu = v;
        if (v && !eskiDeger && this._mevcutSpline) {
            this.mevcutSplineyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Düzenlenecek spline */
    private _mevcutSpline?: FikoYT3dSpline;
    @Input()
    get mevcutSpline(): FikoYT3dSpline | undefined { return this._mevcutSpline; }
    set mevcutSpline(v: FikoYT3dSpline | undefined) {
        this._mevcutSpline = v;
        if (v && this._duzenleModu) {
            this.mevcutSplineyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Dialog görünürlüğü */
    @Input() gorunur: boolean = false;

    /** Spline oluşturulduğunda */
    @Output() splineOlusturuldu = new EventEmitter<FikoYT3dSpline>();

    /** Dialog kapandığında */
    @Output() kapandi = new EventEmitter<void>();

    // ========================================================================
    // FORM DEĞERLERİ
    // ========================================================================

    // Temel
    isim: string = '';
    kapali: boolean = false;

    // Kontrol noktaları listesi
    noktalar: { x: number; y: number; z: number }[] = [
        { x: 0, y: 0, z: 0 },
        { x: 30, y: 40, z: 0 },
        { x: 70, y: -20, z: 0 },
        { x: 100, y: 30, z: 0 }
    ];

    // Seçili nokta indexi
    seciliNoktaIndex: number = -1;

    // Spline tipi
    splineTipi: SplineTipi = 'catmull-rom';
    splineTipiSecenekleri = [
        { deger: 'catmull-rom', etiket: 'Catmull-Rom' },
        { deger: 'bezier', etiket: 'Bezier (Cubic)' },
        { deger: 'bezier-quadratic', etiket: 'Bezier (Quadratic)' }
    ];

    // Catmull-Rom ayarları
    tension: number = 0.5;
    catmullTipi: CatmullRomTensionTipi = 'centripetal';
    catmullTipiSecenekleri = [
        { deger: 'catmullrom', etiket: 'Catmull-Rom' },
        { deger: 'centripetal', etiket: 'Centripetal' },
        { deger: 'chordal', etiket: 'Chordal' }
    ];

    // Segment sayısı (eğri çözünürlüğü)
    segmentSayisi: number = 50;

    // Hazır şekil ayarları
    hazirSekil: HazirSekilTipi = 'serbest';
    sekilMerkezX: number = 50;
    sekilMerkezY: number = 0;
    sekilMerkezZ: number = 0;

    // Sinus parametreleri
    sinusDalgaSayisi: number = 3;
    sinusGenlik: number = 30;
    sinusUzunluk: number = 150;

    // Spiral parametreleri
    spiralBaslangicYaricap: number = 10;
    spiralBitisYaricap: number = 50;
    spiralTurSayisi: number = 3;

    // Elips parametreleri
    elipsYaricapX: number = 60;
    elipsYaricapY: number = 40;
    elipsNoktaSayisi: number = 12;

    // Görünüm ayarları
    renk: string = '#ff6b00';
    kalinlik: number = 2;
    opaklik: number = 1;
    stil: SplineStili = 'duz';
    kesikliAralik: number = 5;
    kesikliBoyut: number = 10;

    // Diğer ayarlar
    noktaGoster: boolean = true;
    noktaBoyutu: number = 6;
    noktaRengi: string = '#00ff00';
    kontrolNoktaGoster: boolean = true;
    kontrolCizgiGoster: boolean = true;
    enUstte: boolean = false;
    renderEt: boolean = true;
    secilebilir: boolean = true;
    katman: number = 0;

    // Stil seçenekleri
    stilSecenekleri = [
        { deger: 'duz', etiket: 'Düz' },
        { deger: 'kesikli', etiket: 'Kesikli' },
        { deger: 'noktali', etiket: 'Noktalı' },
        { deger: 'kesikli-noktali', etiket: 'Kesikli-Noktalı' }
    ];

    // Çizgi tipi
    cizgiTipi: SplineCizgiTipi = 'Line2';
    cizgiTipiSecenekleri = [
        { deger: 'Line2', etiket: 'Line2 (Kalın)' },
        { deger: 'Line', etiket: 'Line (İnce)' },
        { deger: 'LineSegments', etiket: 'LineSegments' }
    ];

    // Aktif tab
    aktifTab: 'noktalar' | 'sekil' | 'egri' | 'stil' | 'diger' = 'noktalar';

    // ========================================================================
    // BODY SEÇİMİ
    // ========================================================================

    /** Seçili aktif body */
    seciliAktifBody: any = null;

    /** Body'ye ekleme aktif mi */
    aktifBodyeEkle: boolean = true;

    /** Body seçme modu aktif mi */
    bodySecModu: boolean = false;

    /** Seçili body'nin görüntülenen ismi */
    get seciliAktifBodyIsim(): string {
        if (!this.seciliAktifBody) return '(Seçilmedi)';
        return this.seciliAktifBody.name || this.seciliAktifBody.isim || 'Body';
    }

    // ========================================================================
    // DÜZLEM SİSTEMİ
    // ========================================================================

    /** Düzlem normal vektörü */
    duzlemNormal: { x: number; y: number; z: number } = { x: 0, y: 0, z: 1 };

    /** Düzlem kamera takip modu */
    duzlemKameraTakip: boolean = true;

    /** Düzlem sabiti */
    duzlemD: number = 0;

    /** THREE.Plane nesnesi */
    private _duzlemPlane: THREE.Plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

    /** Düzlem ayarları paneli görünür mü */
    duzlemAyarGoster: boolean = false;

    /** Düzlem seçme modu */
    duzlemSecModu: boolean = false;

    /** Seçili yüzey ismi */
    seciliYuzeyIsmi: string = '';

    // ========================================================================
    // KOORDİNAT DÜZENLEME
    // ========================================================================

    /** Koordinat dialog açık mı */
    koordinatDialogAcik: boolean = false;

    /** Düzenlenen nokta indexi */
    koordinatDialogIndex: number | null = null;

    // ========================================================================
    // ARDIŞIK TIKLAMA MODU
    // ========================================================================

    /** Ardışık tıklama modu */
    ardisikTiklamaModu: boolean = false;

    /** Ardışık tıklama handler */
    private ardisikTiklamaHandler?: (event: MouseEvent) => void;

    /** Ardışık context menu handler */
    private ardisikContextMenuHandler?: (event: MouseEvent) => void;

    // ========================================================================
    // NOKTA SEÇME MODU
    // ========================================================================

    /** Nokta seçme modu */
    noktaSecModu: number | null = null;

    /** Nokta seçme handler */
    private noktaSecClickHandler?: (event: MouseEvent) => void;

    // ========================================================================
    // PRIVATE MEMBERS
    // ========================================================================

    /** Geçici spline */
    private geciciSpline?: FikoYT3dSpline;
    private geciciSplineAktif: boolean = false;

    /** Handle sistemi */
    private handles?: FikoYT3dSplineHandles;
    private handleAktif: boolean = false;
    private handleRaycaster: THREE.Raycaster = new THREE.Raycaster();
    private dragPlane: THREE.Plane = new THREE.Plane();
    private dragOffset: THREE.Vector3 = new THREE.Vector3();

    /** Event listener referansları */
    private handleMouseDownRef?: (e: MouseEvent) => void;
    private handleMouseMoveRef?: (e: MouseEvent) => void;
    private handleMouseUpRef?: (e: MouseEvent) => void;

    /** Handle retry mekanizması */
    private handleBaslatRetryCount: number = 0;
    private readonly HANDLE_MAX_RETRY = 5;
    private readonly HANDLE_RETRY_DELAY = 100;
    private handleBaslatTimeoutId?: ReturnType<typeof setTimeout>;

    private fikoYT = inject(FikoYTService);
    private ngZone = inject(NgZone);

    constructor(private cd: ChangeDetectorRef) {}

    // ========================================================================
    // COMPUTED PROPERTIES
    // ========================================================================

    /** Toplam uzunluk (cache'li - NG0100 hatasını önler) */
    private _toplamUzunlukCache: string = '0.00';
    get toplamUzunluk(): string {
        return this._toplamUzunlukCache;
    }

    /** Toplam uzunluğu güncelle (ngZone ile change detection tetikler) */
    private toplamUzunlukGuncelle(): void {
        this.ngZone.run(() => {
            const hedefSpline = this.geciciSplineAktif ? this.geciciSpline : this._mevcutSpline;
            if (!hedefSpline) {
                this._toplamUzunlukCache = '0.00';
            } else {
                this._toplamUzunlukCache = hedefSpline.toplamUzunluk.toFixed(2);
            }
        });
    }

    // ========================================================================
    // LIFECYCLE
    // ========================================================================

    ngOnInit(): void {
        // Sahne referansını pencereden al
        if (this._pencere?.bilesenParametreler) {
            const params = this._pencere.bilesenParametreler;
            if (params.sahne) this.sahne = params.sahne;
            if (params.anaGosterim) this.anaGosterim = params.anaGosterim;
            if (params.mevcutSpline) {
                this._mevcutSpline = params.mevcutSpline;
                this._duzenleModu = true;
            }
            if (params.duzenleModu !== undefined) this._duzenleModu = params.duzenleModu;
        }

        // Sahne referansını ayarla
        if (!this.sahne && this.anaGosterim?.sahneler?.[0]) {
            this.sahne = this.anaGosterim.sahneler[0];
        }

        // Global dialog tracking
        FikoYT3dSpline.globalDialogAyarla({ gorunur: true, pencere: this._pencere });

        // Düzenleme modunda değilse geçici spline oluştur
        if (!this._duzenleModu) {
            setTimeout(() => this.geciciSplineOlustur(), 100);
        } else if (this._mevcutSpline) {
            this.mevcutSplineyiYukle();
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this._duzenleModu && this._mevcutSpline) {
                this.handleBaslat();
            } else if (this.geciciSpline) {
                this.handleBaslat();
            }
        }, 200);
    }

    ngOnDestroy(): void {
        FikoYT3dSpline.globalDialogTemizle();
        this.ardisikTiklamaDurdur();
        this.noktaSecDurdur();
        this.handleDurdur();
        this.geciciSplineSil();
        this.tumOrphanHandleTemizle();
    }

    /**
     * Tüm orphan handle'ları temizle
     */
    private tumOrphanHandleTemizle(): void {
        const sahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!sahne) return;

        const silinecekler: THREE.Object3D[] = [];
        sahne.traverse((child: THREE.Object3D) => {
            if (child.name && child.name.startsWith('SplineHandles_')) {
                silinecekler.push(child);
            }
            if (child.name && child.name.startsWith('SplineHandle_')) {
                silinecekler.push(child);
            }
            if (child.name === 'Geçici_Spline') {
                silinecekler.push(child);
            }
        });

        silinecekler.forEach(obj => {
            if (obj.parent) {
                obj.parent.remove(obj);
            }
            obj.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    if (mesh.geometry) mesh.geometry.dispose();
                    if (mesh.material) {
                        if (Array.isArray(mesh.material)) {
                            mesh.material.forEach(m => m.dispose());
                        } else {
                            mesh.material.dispose();
                        }
                    }
                }
                if ((child as any).isLine2) {
                    const line = child as any;
                    if (line.geometry) line.geometry.dispose();
                    if (line.material) line.material.dispose();
                }
            });
        });
    }

    // ========================================================================
    // DIALOG YÖNETİMİ
    // ========================================================================

    ac(): void {
        this.gorunur = true;

        if (!this._duzenleModu && !this.geciciSpline) {
            this.geciciSplineOlustur();
        }

        setTimeout(() => this.handleBaslat(), 100);
    }

    kapat(): void {
        FikoYT3dSpline.globalDialogTemizle();
        this.ardisikTiklamaDurdurInternal();
        this.noktaSecDurdur();
        this.handleDurdur();
        this.geciciSplineSil();
        this.tumOrphanHandleTemizle();

        this.gorunur = false;

        if (this._pencere?.kapa) {
            this._pencere.kapa();
        }

        this.kapandi.emit();
    }

    private dialogYenidenAcildi(): void {
        this.handleDurdur();

        if (this._mevcutSpline) {
            this.mevcutSplineyiYukle();
        }

        setTimeout(() => this.handleBaslat(), 100);
    }

    private yeniSplineModuBaslat(): void {
        this.handleDurdur();
        this.geciciSplineSil();
        this.formDegerleriniSifirla();

        setTimeout(() => {
            this.geciciSplineOlustur();
        }, 50);
    }

    private formDegerleriniSifirla(): void {
        this.isim = '';
        this.kapali = false;
        this.noktalar = [
            { x: 0, y: 0, z: 0 },
            { x: 30, y: 40, z: 0 },
            { x: 70, y: -20, z: 0 },
            { x: 100, y: 30, z: 0 }
        ];
        this.seciliNoktaIndex = -1;
        this.hazirSekil = 'serbest';
        this.splineTipi = 'catmull-rom';
        this.tension = 0.5;
        this.segmentSayisi = 50;
        this.renk = '#ff6b00';
        this.kalinlik = 2;
        this.opaklik = 1;
        this.stil = 'duz';
        this.noktaGoster = true;
        this.kontrolNoktaGoster = true;
        this.kontrolCizgiGoster = true;
        this.enUstte = false;
        this.renderEt = true;
        this.secilebilir = true;
        this.katman = 0;
    }

    // ========================================================================
    // GEÇİCİ SPLINE
    // ========================================================================

    private geciciSplineOlustur(): void {
        const hedefSahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!hedefSahne) {
            console.warn('[FikoYT3dSpline] Sahne bulunamadı');
            return;
        }

        this.sahne = hedefSahne;
        this.geciciSplineSil();

        this.geciciSpline = new FikoYT3dSpline(hedefSahne, 'Geçici_Spline');

        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        this.geciciSpline.noktalarileBelirle(noktalarVec, this.kapali);

        this.ayarlariUygula(this.geciciSpline);

        this.sahne.add(this.geciciSpline);
        this.geciciSplineAktif = true;

        setTimeout(() => {
            if (this.geciciSpline) {
                this.geciciSpline.materyaliTazele();
            }
        }, 10);

        setTimeout(() => {
            if (this.geciciSpline) {
                this.handles = this.geciciSpline.handlelerGoster();
                this.handleBaslat();
                // Toplam uzunluk cache'ini güncelle
                this.toplamUzunlukGuncelle();
            }
        }, 50);
    }

    private geciciSplineSil(): void {
        if (this.geciciSpline) {
            this.geciciSpline.handlelerGizle();
            this.geciciSpline.sil();
            this.geciciSpline = undefined;
            this.geciciSplineAktif = false;
        }
    }

    private geciciSplineGuncelle(): void {
        const hedefSpline = this.geciciSplineAktif ? this.geciciSpline : this._mevcutSpline;
        if (!hedefSpline) return;

        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        hedefSpline.noktalarileBelirle(noktalarVec, this.kapali);

        this.ayarlariUygula(hedefSpline);
        hedefSpline.materyaliTazele();

        if (this.handles) {
            this.handles.guncelle();
        }

        // Toplam uzunluk cache'ini güncelle
        this.toplamUzunlukGuncelle();
    }

    // ========================================================================
    // MEVCUT SPLINE DÜZENLEME
    // ========================================================================

    private mevcutSplineyiYukle(): void {
        if (!this._mevcutSpline) return;

        this.isim = this._mevcutSpline.name;
        this.kapali = this._mevcutSpline.kapali;

        this.noktalar = this._mevcutSpline.kontrolNoktalar.map(n => ({
            x: Math.round(n.x * 100) / 100,
            y: Math.round(n.y * 100) / 100,
            z: Math.round(n.z * 100) / 100
        }));

        this.splineTipi = this._mevcutSpline.splineTipi;
        this.tension = this._mevcutSpline.tension;
        this.catmullTipi = this._mevcutSpline.catmullTipi;
        this.segmentSayisi = this._mevcutSpline.segmentSayisi;

        this.cizgiTipi = this._mevcutSpline.cizgiTipi;
        const renkHex = typeof this._mevcutSpline.renk === 'number'
            ? '#' + this._mevcutSpline.renk.toString(16).padStart(6, '0')
            : this._mevcutSpline.renk as string;
        this.renk = renkHex;
        this.kalinlik = this._mevcutSpline.kalinlik;
        this.opaklik = this._mevcutSpline.opaklık;
        this.stil = this._mevcutSpline.stil;
        this.noktaGoster = this._mevcutSpline.noktaGoster;
        this.kontrolNoktaGoster = this._mevcutSpline.kontrolNoktaGoster;
        this.kontrolCizgiGoster = this._mevcutSpline.kontrolCizgiGoster;
        this.enUstte = this._mevcutSpline.enUstte;
        this.renderEt = this._mevcutSpline.renderEt;
        this.secilebilir = this._mevcutSpline.secilebilir;

        // Toplam uzunluk cache'ini güncelle
        this.toplamUzunlukGuncelle();

        this.cd.detectChanges();
    }

    // ========================================================================
    // AYAR UYGULAMA
    // ========================================================================

    private ayarlariUygula(spline: FikoYT3dSpline): void {
        spline.splineTipi = this.splineTipi;
        spline.tension = this.tension;
        spline.catmullTipi = this.catmullTipi;
        spline.segmentSayisi = this.segmentSayisi;
        spline.cizgiTipi = this.cizgiTipi;
        spline.renk = this.renk;
        spline.kalinlik = this.kalinlik;
        spline.opaklık = this.opaklik;
        spline.stil = this.stil;
        spline.noktaGoster = this.noktaGoster;
        spline.kontrolNoktaGoster = this.kontrolNoktaGoster;
        spline.kontrolCizgiGoster = this.kontrolCizgiGoster;
        spline.enUstte = this.enUstte;
        spline.renderEt = this.renderEt;
        spline.secilebilir = this.secilebilir;
        spline.kapali = this.kapali;
    }

    // ========================================================================
    // NOKTA YÖNETİMİ
    // ========================================================================

    noktaEkle(): void {
        const sonNokta = this.noktalar[this.noktalar.length - 1];
        let yeniNoktaVec = new THREE.Vector3(sonNokta.x + 25, sonNokta.y + 15, sonNokta.z);
        yeniNoktaVec = this.noktayiDuzlemeProject(yeniNoktaVec);

        const yeniNokta = {
            x: Math.round(yeniNoktaVec.x * 100) / 100,
            y: Math.round(yeniNoktaVec.y * 100) / 100,
            z: Math.round(yeniNoktaVec.z * 100) / 100
        };
        this.noktalar.push(yeniNokta);
        this.seciliNoktaIndex = this.noktalar.length - 1;
        this.geciciSplineGuncelle();
    }

    arayaNoktaEkle(index: number): void {
        if (index < 0 || index >= this.noktalar.length - 1) return;

        const p1 = this.noktalar[index];
        const p2 = this.noktalar[index + 1];
        let ortaVec = new THREE.Vector3(
            (p1.x + p2.x) / 2,
            (p1.y + p2.y) / 2,
            (p1.z + p2.z) / 2
        );
        ortaVec = this.noktayiDuzlemeProject(ortaVec);

        const orta = {
            x: Math.round(ortaVec.x * 100) / 100,
            y: Math.round(ortaVec.y * 100) / 100,
            z: Math.round(ortaVec.z * 100) / 100
        };
        this.noktalar.splice(index + 1, 0, orta);
        this.seciliNoktaIndex = index + 1;
        this.geciciSplineGuncelle();
    }

    noktaSil(index: number): void {
        if (this.noktalar.length <= 2) {
            alert('En az 2 kontrol noktası olmalı!');
            return;
        }
        this.noktalar.splice(index, 1);
        if (this.seciliNoktaIndex >= this.noktalar.length) {
            this.seciliNoktaIndex = this.noktalar.length - 1;
        }
        this.geciciSplineGuncelle();
    }

    noktaSec(index: number): void {
        this.seciliNoktaIndex = index;
    }

    noktaDegisti(): void {
        this.geciciSplineGuncelle();
    }

    noktaDuzenleDialogAc(index: number): void {
        this.koordinatDialogIndex = index;
        this.koordinatDialogAcik = true;
        this.seciliNoktaIndex = index;
        this.cd.detectChanges();
    }

    koordinatDialogKapat(): void {
        this.koordinatDialogAcik = false;
        this.koordinatDialogIndex = null;
        this.cd.detectChanges();
    }

    // ========================================================================
    // EĞRİ AYARLARI
    // ========================================================================

    splineTipiDegisti(): void {
        this.geciciSplineGuncelle();
    }

    tensionDegisti(): void {
        this.geciciSplineGuncelle();
    }

    catmullTipiDegisti(): void {
        this.geciciSplineGuncelle();
    }

    segmentSayisiDegisti(): void {
        this.geciciSplineGuncelle();
    }

    kapaliDegisti(): void {
        this.geciciSplineGuncelle();
    }

    // ========================================================================
    // HAZIR ŞEKİLLER
    // ========================================================================

    hazirSekilDegisti(): void {
        this.hazirSekilOlustur();
    }

    sekilParametresiDegisti(): void {
        if (this.hazirSekil !== 'serbest') {
            this.hazirSekilOlustur();
        }
    }

    private hazirSekilOlustur(): void {
        const merkez = new THREE.Vector3(this.sekilMerkezX, this.sekilMerkezY, this.sekilMerkezZ);

        switch (this.hazirSekil) {
            case 'sinus':
                this.sinusOlustur(merkez);
                break;
            case 'spiral':
                this.spiralOlustur(merkez);
                break;
            case 'elips':
                this.elipsOlustur(merkez);
                break;
            case 'kalp':
                this.kalpOlustur(merkez);
                break;
            case 'sonsuzluk':
                this.sonsuzlukOlustur(merkez);
                break;
            default:
                break;
        }

        this.geciciSplineGuncelle();
    }

    private sinusOlustur(merkez: THREE.Vector3): void {
        const noktalar: { x: number; y: number; z: number }[] = [];
        const noktaSayisi = this.sinusDalgaSayisi * 4 + 1;

        for (let i = 0; i < noktaSayisi; i++) {
            const t = i / (noktaSayisi - 1);
            const x = merkez.x + t * this.sinusUzunluk - this.sinusUzunluk / 2;
            const y = merkez.y + Math.sin(t * Math.PI * 2 * this.sinusDalgaSayisi) * this.sinusGenlik;
            noktalar.push({ x, y, z: merkez.z });
        }

        this.noktalar = noktalar;
        this.kapali = false;
    }

    private spiralOlustur(merkez: THREE.Vector3): void {
        const noktalar: { x: number; y: number; z: number }[] = [];
        const noktaSayisi = this.spiralTurSayisi * 8;

        for (let i = 0; i <= noktaSayisi; i++) {
            const t = i / noktaSayisi;
            const aci = t * Math.PI * 2 * this.spiralTurSayisi;
            const yaricap = this.spiralBaslangicYaricap + (this.spiralBitisYaricap - this.spiralBaslangicYaricap) * t;

            noktalar.push({
                x: merkez.x + Math.cos(aci) * yaricap,
                y: merkez.y + Math.sin(aci) * yaricap,
                z: merkez.z
            });
        }

        this.noktalar = noktalar;
        this.kapali = false;
    }

    private elipsOlustur(merkez: THREE.Vector3): void {
        const noktalar: { x: number; y: number; z: number }[] = [];

        for (let i = 0; i < this.elipsNoktaSayisi; i++) {
            const aci = (i / this.elipsNoktaSayisi) * Math.PI * 2;
            noktalar.push({
                x: merkez.x + Math.cos(aci) * this.elipsYaricapX,
                y: merkez.y + Math.sin(aci) * this.elipsYaricapY,
                z: merkez.z
            });
        }

        this.noktalar = noktalar;
        this.kapali = true;
    }

    private kalpOlustur(merkez: THREE.Vector3): void {
        const noktalar: { x: number; y: number; z: number }[] = [];
        const noktaSayisi = 16;
        const olcek = 25;

        for (let i = 0; i < noktaSayisi; i++) {
            const t = (i / noktaSayisi) * Math.PI * 2;
            // Kalp parametrik denklemi
            const x = 16 * Math.pow(Math.sin(t), 3);
            const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);

            noktalar.push({
                x: merkez.x + x * olcek / 16,
                y: merkez.y + y * olcek / 16,
                z: merkez.z
            });
        }

        this.noktalar = noktalar;
        this.kapali = true;
    }

    private sonsuzlukOlustur(merkez: THREE.Vector3): void {
        const noktalar: { x: number; y: number; z: number }[] = [];
        const noktaSayisi = 24;
        const a = 50;

        for (let i = 0; i < noktaSayisi; i++) {
            const t = (i / noktaSayisi) * Math.PI * 2;
            // Lemniscate of Bernoulli
            const denom = 1 + Math.pow(Math.sin(t), 2);
            const x = a * Math.cos(t) / denom;
            const y = a * Math.sin(t) * Math.cos(t) / denom;

            noktalar.push({
                x: merkez.x + x,
                y: merkez.y + y,
                z: merkez.z
            });
        }

        this.noktalar = noktalar;
        this.kapali = true;
    }

    // ========================================================================
    // DÜZLEM İŞLEMLERİ
    // ========================================================================

    private noktayiDuzlemeProject(nokta: THREE.Vector3): THREE.Vector3 {
        if (this.duzlemKameraTakip) {
            return nokta;
        }

        const normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z).normalize();
        this._duzlemPlane.setFromNormalAndCoplanarPoint(normal, new THREE.Vector3(0, 0, -this.duzlemD / normal.z || 0));

        const projected = new THREE.Vector3();
        this._duzlemPlane.projectPoint(nokta, projected);
        return projected;
    }

    duzlemGuncelle(normalize: boolean = true): void {
        if (normalize) {
            this.duzlemNormalize();
        }
        this._duzlemPlane.normal.set(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
        this._duzlemPlane.constant = this.duzlemD;
    }

    duzlemNormalize(): void {
        const len = Math.sqrt(
            this.duzlemNormal.x * this.duzlemNormal.x +
            this.duzlemNormal.y * this.duzlemNormal.y +
            this.duzlemNormal.z * this.duzlemNormal.z
        );
        if (len > 0) {
            this.duzlemNormal.x /= len;
            this.duzlemNormal.y /= len;
            this.duzlemNormal.z /= len;
        }
    }

    duzlemKameraModuAc(): void {
        this.duzlemKameraTakip = true;
        this.seciliYuzeyIsmi = '';
    }

    // ========================================================================
    // STİL DEĞİŞİKLİKLERİ
    // ========================================================================

    renkDegisti(): void {
        this.geciciSplineGuncelle();
    }

    kalinlikDegisti(): void {
        this.geciciSplineGuncelle();
    }

    opaklikDegisti(): void {
        this.geciciSplineGuncelle();
    }

    stilDegisti(): void {
        this.geciciSplineGuncelle();
    }

    cizgiTipiDegisti(): void {
        this.geciciSplineGuncelle();
    }

    noktaGosterDegisti(): void {
        this.geciciSplineGuncelle();
    }

    kontrolNoktaGosterDegisti(): void {
        this.geciciSplineGuncelle();
        if (this.handles) {
            this.handles.guncelle();
        }
    }

    kontrolCizgiGosterDegisti(): void {
        this.geciciSplineGuncelle();
        if (this.handles) {
            this.handles.guncelle();
        }
    }

    enUsteDegisti(): void {
        this.geciciSplineGuncelle();
    }

    renderEtDegisti(): void {
        this.geciciSplineGuncelle();
    }

    secilebilirDegisti(): void {
        this.geciciSplineGuncelle();
    }

    // ========================================================================
    // OLUŞTUR / GÜNCELLE
    // ========================================================================

    olustur(): void {
        if (this.noktalar.length < 2) {
            alert('En az 2 kontrol noktası gerekli!');
            return;
        }

        const sahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!sahne) {
            console.warn('[FikoYT3dSpline] Sahne bulunamadı');
            return;
        }

        // Yeni spline oluştur
        const yeniSpline = new FikoYT3dSpline(sahne, this.isim || undefined);

        // Noktaları ayarla
        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        yeniSpline.noktalarileBelirle(noktalarVec, this.kapali);

        // Ayarları uygula
        this.ayarlariUygula(yeniSpline);

        // Body'ye ekle veya sahneye ekle
        if (this.aktifBodyeEkle && this.seciliAktifBody) {
            this.splineyiBodyeEkle(yeniSpline, this.seciliAktifBody);
        } else {
            // Sahneye doğrudan ekle
            this.splineyiSahneyeEkle(yeniSpline, sahne);
        }

        // Materyali tazele
        yeniSpline.materyaliTazele();

        // Emit
        this.splineOlusturuldu.emit(yeniSpline);

        // Dialog'u kapat
        this.kapat();
    }

    /**
     * Spline'ı seçili body'ye ekle (ürün ağacında görünmesi için)
     */
    private splineyiBodyeEkle(spline: FikoYT3dSpline, body: any): void {
        if (!spline || !body) return;

        try {
            // Sahne referansını ayarla
            const sahneRef = body.sahne || this.sahne || this.anaGosterim?.sahneler?.[0];
            if (sahneRef) {
                (spline as any).sahne = sahneRef;
            }

            // userData.FikoArayuz'u ayarla (ürün ağacında görünmesi için)
            if (!spline.userData) spline.userData = {};
            spline.userData.FikoArayuz = {
                agactaGorunur: true,
                listeGoster: false,
                simge: '/simgeler/line.webp'
            };

            // Body bir THREE.Object3D ise doğrudan ekle
            if (body.add && typeof body.add === 'function') {
                body.add(spline);
            }

            // Body'nin liste array'ine de ekle (ürün ağacı navigasyonu için)
            try {
                if (body._liste && Array.isArray(body._liste)) {
                    if (!body._liste.includes(spline)) {
                        body._liste.push(spline);
                    }
                } else if (body.liste && Array.isArray(body.liste)) {
                    if (!body.liste.includes(spline)) {
                        body.liste.push(spline);
                    }
                }
            } catch {
                // Liste ekleme hatası - görmezden gel
            }

            // FikoYT.degisti emit et - ürün ağacının güncellenmesi için
            this.fikoYT.degisti.emit();

            // Change detection tetikle
            this.cd.detectChanges();
        } catch {
            // Body'ye ekleme hatası - görmezden gel
        }
    }

    /**
     * Spline'ı sahneye ekle (ürün ağacında görünmesi için)
     * NOT: Ürün ağacında görünmesi için sahne.urunAgaci'na eklenir
     */
    private splineyiSahneyeEkle(spline: FikoYT3dSpline, sahne: FikoSahne): void {
        if (!spline || !sahne) return;

        try {
            // Sahne referansını ayarla
            (spline as any).sahne = sahne;

            // userData.FikoArayuz'u ayarla (ürün ağacında görünmesi için)
            if (!spline.userData) spline.userData = {};
            spline.userData.FikoArayuz = {
                agactaGorunur: true,
                listeGoster: false,
                simge: '/simgeler/line.webp'
            };

            // Ürün ağacına ekle (THREE.Scene'e değil, urunAgaci'na!)
            // Bu sayede ürün ağacı listesinde görünür
            const urunAgaci = (sahne as any).urunAgaci;
            if (urunAgaci && typeof urunAgaci.add === 'function') {
                urunAgaci.add(spline);
            } else {
                // Fallback: Direkt sahneye ekle (ürün ağacında görünmez)
                sahne.add(spline);
            }

            // FikoYT.degisti emit et - ürün ağacının güncellenmesi için
            this.fikoYT.degisti.emit();

            // Change detection tetikle
            this.cd.detectChanges();
        } catch {
            // Sahneye ekleme hatası - görmezden gel
        }
    }

    guncelle(): void {
        if (!this._mevcutSpline) return;

        // Noktaları güncelle
        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        this._mevcutSpline.noktalarileBelirle(noktalarVec, this.kapali);

        // İsmi güncelle
        if (this.isim) {
            this._mevcutSpline.name = this.isim;
            this._mevcutSpline.etiket = this.isim;
        }

        // Ayarları uygula
        this.ayarlariUygula(this._mevcutSpline);

        // Materyali tazele
        this._mevcutSpline.materyaliTazele();

        // Dialog'u kapat
        this.kapat();
    }

    sifirla(): void {
        this.formDegerleriniSifirla();
        this.geciciSplineGuncelle();
    }

    // ========================================================================
    // HANDLE SİSTEMİ
    // ========================================================================

    private handleBaslat(): void {
        const hedefSpline = this.geciciSplineAktif ? this.geciciSpline : this._mevcutSpline;
        if (!hedefSpline) {
            if (this.handleBaslatRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleBaslatRetryCount++;
                this.handleBaslatTimeoutId = setTimeout(() => this.handleBaslat(), this.HANDLE_RETRY_DELAY);
            }
            return;
        }

        this.handleBaslatRetryCount = 0;

        this.handles = hedefSpline.handlelerGoster();
        if (!this.handles) return;

        this.handleAktif = true;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (!canvas) return;

        this.handleMouseDownRef = (e: MouseEvent) => this.onHandleMouseDown(e);
        this.handleMouseMoveRef = (e: MouseEvent) => this.onHandleMouseMove(e);
        this.handleMouseUpRef = (e: MouseEvent) => this.onHandleMouseUp(e);

        canvas.addEventListener('mousedown', this.handleMouseDownRef);
        canvas.addEventListener('mousemove', this.handleMouseMoveRef);
        canvas.addEventListener('mouseup', this.handleMouseUpRef);
    }

    private handleDurdur(): void {
        if (this.handleBaslatTimeoutId) {
            clearTimeout(this.handleBaslatTimeoutId);
            this.handleBaslatTimeoutId = undefined;
        }

        this.handleAktif = false;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (canvas) {
            if (this.handleMouseDownRef) {
                canvas.removeEventListener('mousedown', this.handleMouseDownRef);
            }
            if (this.handleMouseMoveRef) {
                canvas.removeEventListener('mousemove', this.handleMouseMoveRef);
            }
            if (this.handleMouseUpRef) {
                canvas.removeEventListener('mouseup', this.handleMouseUpRef);
            }
        }

        this.handleMouseDownRef = undefined;
        this.handleMouseMoveRef = undefined;
        this.handleMouseUpRef = undefined;

        // Handle'ları gizle
        const hedefSpline = this.geciciSplineAktif ? this.geciciSpline : this._mevcutSpline;
        if (hedefSpline) {
            hedefSpline.handlelerGizle();
        }

        this.handles = undefined;
    }

    private onHandleMouseDown(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;
        if (event.button !== 0) return;

        const canvas = event.target as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const kamera = this.sahne?.anaGosterim?.aktifKamera || this.anaGosterim?.aktifKamera;
        if (!kamera) return;

        const result = this.handles.noktadanHandleBul(mouse.x, mouse.y, kamera, 30);

        if (result) {
            this.handles.isDragging = true;
            this.handles.dragTarget = result.index;
            this.seciliNoktaIndex = result.index;

            // Drag plane oluştur
            const handleNokta = this.noktalar[result.index];
            const handlePos = new THREE.Vector3(handleNokta.x, handleNokta.y, handleNokta.z);
            const kameraYonu = new THREE.Vector3();
            kamera.getWorldDirection(kameraYonu);
            this.dragPlane.setFromNormalAndCoplanarPoint(kameraYonu.negate(), handlePos);

            // Offset hesapla
            this.handleRaycaster.setFromCamera(mouse, kamera);
            const intersect = new THREE.Vector3();
            this.handleRaycaster.ray.intersectPlane(this.dragPlane, intersect);
            this.dragOffset.copy(handlePos).sub(intersect);

            // Orbit controls'u devre dışı bırak
            const kontrol = this.sahne?.anaGosterim?.aktifKontrol || this.anaGosterim?.aktifKontrol;
            if (kontrol) {
                kontrol.enabled = false;
            }

            this.cd.detectChanges();
        }
    }

    private onHandleMouseMove(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;

        if (!this.handles.isDragging || this.handles.dragTarget === null) return;

        const canvas = event.target as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const kamera = this.sahne?.anaGosterim?.aktifKamera || this.anaGosterim?.aktifKamera;
        if (!kamera) return;

        this.handleRaycaster.setFromCamera(mouse, kamera);
        const intersect = new THREE.Vector3();

        if (this.handleRaycaster.ray.intersectPlane(this.dragPlane, intersect)) {
            const yeniPoz = intersect.add(this.dragOffset);

            // Düzleme project et
            const projectedPoz = this.noktayiDuzlemeProject(yeniPoz);

            // Noktayı güncelle
            this.noktalar[this.handles.dragTarget] = {
                x: Math.round(projectedPoz.x * 100) / 100,
                y: Math.round(projectedPoz.y * 100) / 100,
                z: Math.round(projectedPoz.z * 100) / 100
            };

            this.geciciSplineGuncelle();
            this.cd.detectChanges();
        }
    }

    private onHandleMouseUp(event: MouseEvent): void {
        if (!this.handles) return;

        this.handles.isDragging = false;
        this.handles.dragTarget = null;

        // Orbit controls'u geri aç
        const kontrol = this.sahne?.anaGosterim?.aktifKontrol || this.anaGosterim?.aktifKontrol;
        if (kontrol) {
            kontrol.enabled = true;
        }
    }

    // ========================================================================
    // ARDIŞIK TIKLAMA MODU
    // ========================================================================

    ardisikTiklamaToggle(): void {
        if (this.ardisikTiklamaModu) {
            this.ardisikTiklamaDurdur();
        } else {
            this.ardisikTiklamaBaslat();
        }
    }

    private ardisikTiklamaBaslat(): void {
        this.ardisikTiklamaModu = true;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (!canvas) return;

        this.ardisikTiklamaHandler = (event: MouseEvent) => {
            if (event.button !== 0) return;

            const rect = canvas.getBoundingClientRect();
            const mouse = new THREE.Vector2(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1
            );

            const kamera = this.sahne?.anaGosterim?.aktifKamera || this.anaGosterim?.aktifKamera;
            if (!kamera) return;

            // Ray oluştur
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse, kamera);

            // Düzlem ile kesişim
            const duzlem = this.duzlemKameraTakip
                ? new THREE.Plane(new THREE.Vector3(0, 0, 1).applyQuaternion(kamera.quaternion), 0)
                : new THREE.Plane(new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z), this.duzlemD);

            const intersect = new THREE.Vector3();
            if (raycaster.ray.intersectPlane(duzlem, intersect)) {
                this.noktalar.push({
                    x: Math.round(intersect.x * 100) / 100,
                    y: Math.round(intersect.y * 100) / 100,
                    z: Math.round(intersect.z * 100) / 100
                });
                this.seciliNoktaIndex = this.noktalar.length - 1;
                this.geciciSplineGuncelle();
                this.cd.detectChanges();
            }
        };

        this.ardisikContextMenuHandler = (event: MouseEvent) => {
            event.preventDefault();
            this.ardisikTiklamaDurdur();
        };

        canvas.addEventListener('click', this.ardisikTiklamaHandler);
        canvas.addEventListener('contextmenu', this.ardisikContextMenuHandler);
    }

    private ardisikTiklamaDurdur(): void {
        this.ardisikTiklamaDurdurInternal();
        this.cd.detectChanges();
    }

    private ardisikTiklamaDurdurInternal(): void {
        this.ardisikTiklamaModu = false;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (canvas) {
            if (this.ardisikTiklamaHandler) {
                canvas.removeEventListener('click', this.ardisikTiklamaHandler);
            }
            if (this.ardisikContextMenuHandler) {
                canvas.removeEventListener('contextmenu', this.ardisikContextMenuHandler);
            }
        }

        this.ardisikTiklamaHandler = undefined;
        this.ardisikContextMenuHandler = undefined;
    }

    // ========================================================================
    // NOKTA SEÇME MODU
    // ========================================================================

    noktaSecBaslat(index: number): void {
        if (this.noktaSecModu === index) {
            this.noktaSecDurdur();
            return;
        }

        this.noktaSecModu = index;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (!canvas) return;

        // Önceki handler'ı temizle
        if (this.noktaSecClickHandler) {
            canvas.removeEventListener('click', this.noktaSecClickHandler);
        }

        this.noktaSecClickHandler = (event: MouseEvent) => {
            if (event.button !== 0) return;

            const rect = canvas.getBoundingClientRect();
            const mouse = new THREE.Vector2(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1
            );

            const kamera = this.sahne?.anaGosterim?.aktifKamera || this.anaGosterim?.aktifKamera;
            if (!kamera) return;

            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse, kamera);

            const duzlem = this.duzlemKameraTakip
                ? new THREE.Plane(new THREE.Vector3(0, 0, 1).applyQuaternion(kamera.quaternion), 0)
                : new THREE.Plane(new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z), this.duzlemD);

            const intersect = new THREE.Vector3();
            if (raycaster.ray.intersectPlane(duzlem, intersect)) {
                if (this.noktaSecModu !== null && this.noktaSecModu < this.noktalar.length) {
                    // Mevcut noktayı güncelle
                    this.noktalar[this.noktaSecModu] = {
                        x: Math.round(intersect.x * 100) / 100,
                        y: Math.round(intersect.y * 100) / 100,
                        z: Math.round(intersect.z * 100) / 100
                    };
                } else if (this.noktaSecModu === this.noktalar.length) {
                    // Yeni nokta ekle
                    this.noktalar.push({
                        x: Math.round(intersect.x * 100) / 100,
                        y: Math.round(intersect.y * 100) / 100,
                        z: Math.round(intersect.z * 100) / 100
                    });
                    this.seciliNoktaIndex = this.noktalar.length - 1;
                }

                this.geciciSplineGuncelle();
                this.noktaSecDurdur();
                this.cd.detectChanges();
            }
        };

        canvas.addEventListener('click', this.noktaSecClickHandler, { once: true });
    }

    private noktaSecDurdur(): void {
        this.noktaSecModu = null;

        const canvas = this.sahne?.anaGosterim?.canvas?.nativeElement ||
                       this.anaGosterim?.canvas?.nativeElement ||
                       this.sahne?.anaGosterim?.sunucu?.domElement;

        if (canvas && this.noktaSecClickHandler) {
            canvas.removeEventListener('click', this.noktaSecClickHandler);
        }

        this.noktaSecClickHandler = undefined;
    }
}
